import { useState, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import {
  DataTable,
  DataTableColumn,
  FilterField,
} from '../../components/ui/data-table';
import { Badge } from '../../components/ui/badge';
import InlineFilter from '../../components/ui/inline-filter';
import PageHeader from '../../components/common/PageHeader';
import StatsCard from '../../components/common/StatsCard';
import ActionDropdown from '../../components/common/action-dropdown';
import { getStatusColor } from '../../utils/helpers';
import {
  Download,
  Filter,
  DollarSign,
  TrendingUp,
  Gift,
  Users,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';

interface Rebate {
  id: string;
  type: 'rebate' | 'bonus';
  clientName: string;
  amount: string;
  status: 'active' | 'inactive' | 'pending';
  campaign: string;
  assignedIB: string;
  startDate: string;
  endDate?: string;
}

const IBRebates = () => {
  const [searchValue, setSearchValue] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );

  const rebates: Rebate[] = [
    {
      id: '1',
      type: 'rebate',
      clientName: 'John Smith',
      amount: '$150.00',
      status: 'active',
      campaign: 'Spring Trading Bonus',
      assignedIB: 'John Anderson',
      startDate: '2024-03-01',
      endDate: '2024-03-31',
    },
    {
      id: '2',
      type: 'bonus',
      clientName: 'Sarah Johnson',
      amount: '$200.00',
      status: 'active',
      campaign: 'New Client Welcome Bonus',
      assignedIB: 'John Anderson',
      startDate: '2024-02-15',
      endDate: '2024-04-15',
    },
    {
      id: '3',
      type: 'rebate',
      clientName: 'Mike Williams',
      amount: '$75.00',
      status: 'pending',
      campaign: 'Volume Rebate Program',
      assignedIB: 'Michael Chen',
      startDate: '2024-03-10',
    },
    {
      id: '4',
      type: 'bonus',
      clientName: 'Emily Davis',
      amount: '$500.00',
      status: 'active',
      campaign: 'VIP Platinum Bonus',
      assignedIB: 'Michael Chen',
      startDate: '2024-01-01',
      endDate: '2024-12-31',
    },
    {
      id: '5',
      type: 'rebate',
      clientName: 'Robert Brown',
      amount: '$125.00',
      status: 'active',
      campaign: 'Spring Trading Bonus',
      assignedIB: 'John Anderson',
      startDate: '2024-03-01',
      endDate: '2024-03-31',
    },
    {
      id: '6',
      type: 'bonus',
      clientName: 'Jennifer Lee',
      amount: '$180.00',
      status: 'inactive',
      campaign: 'New Client Welcome Bonus',
      assignedIB: 'Sarah Mitchell',
      startDate: '2024-01-20',
      endDate: '2024-02-20',
    },
    {
      id: '7',
      type: 'rebate',
      clientName: 'David Wilson',
      amount: '$50.00',
      status: 'pending',
      campaign: 'Volume Rebate Program',
      assignedIB: 'Lisa Wang',
      startDate: '2024-03-05',
    },
    {
      id: '8',
      type: 'bonus',
      clientName: 'Amanda Taylor',
      amount: '$750.00',
      status: 'active',
      campaign: 'VIP Platinum Bonus',
      assignedIB: 'Michael Chen',
      startDate: '2024-01-01',
      endDate: '2024-12-31',
    },
  ];

  const filterFields: FilterField[] = [
    {
      id: 'type',
      label: 'Type',
      options: [
        { value: 'rebate', label: 'Rebate' },
        { value: 'bonus', label: 'Bonus' },
      ],
    },
    {
      id: 'status',
      label: 'Status',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'pending', label: 'Pending' },
      ],
    },
  ];

  const filteredRebates = useMemo(() => {
    return rebates.filter(rebate => {
      if (activeFilters.type && rebate.type !== activeFilters.type) {
        return false;
      }
      if (activeFilters.status && rebate.status !== activeFilters.status) {
        return false;
      }
      if (searchValue) {
        const searchLower = searchValue.toLowerCase();
        return (
          rebate.clientName.toLowerCase().includes(searchLower) ||
          rebate.campaign.toLowerCase().includes(searchLower) ||
          rebate.assignedIB.toLowerCase().includes(searchLower)
        );
      }
      return true;
    });
  }, [rebates, activeFilters, searchValue]);

  const columns: DataTableColumn<Rebate>[] = [
    {
      id: 'type',
      header: 'Type',
      accessor: row => (
        <Badge className={getStatusColor(row.type)}>{row.type}</Badge>
      ),
      sortable: true,
      sortValue: row => row.type,
      width: '120px',
    },
    {
      id: 'clientName',
      header: 'Client Name',
      accessor: row => row.clientName,
      sortable: true,
      sortValue: row => row.clientName,
      width: '120px',
    },
    {
      id: 'campaign',
      header: 'Campaign',
      accessor: row => row.campaign,
      sortable: true,
      sortValue: row => row.campaign,
      width: '160px',
    },
    {
      id: 'amount',
      header: 'Amount',
      accessor: row => row.amount,
      sortable: true,
      sortValue: row => row.amount,
      width: '120px',
    },
    {
      id: 'assignedIB',
      header: 'Assigned IB',
      accessor: row => row.assignedIB,
      sortable: true,
      sortValue: row => row.assignedIB,
      width: '150px',
    },
    {
      id: 'startDate',
      header: 'Start Date',
      accessor: row => row.startDate,
      sortable: true,
      sortValue: row => row.startDate,
      width: '140px',
    },
    {
      id: 'endDate',
      header: 'End Date',
      accessor: row => row.endDate || '-',
      sortable: true,
      sortValue: row => row.endDate || '',
      width: '140px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => (
        <Badge className={getStatusColor(row.status)}>{row.status}</Badge>
      ),
      sortable: true,
      sortValue: row => row.status,
      width: '120px',
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: row => (
        <ActionDropdown
          id={row.id}
          size="sm"
          variant="ghost"
          showView={true}
          showEdit={true}
          showDelete={false}
          showDeposit={false}
          showWithdraw={false}
          onView={() => console.log('View rebate:', row.id)}
          onEdit={() => console.log('Edit rebate:', row.id)}
        />
      ),
      width: '120px',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="IB Rebates"
          description="Manage rebate configurations and bonus campaigns"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          title="Total Rebates"
          value="$0.00"
          change="+0% from last month"
          icon={DollarSign}
        />
        <StatsCard
          title="Active Campaigns"
          value="0"
          change="+0% from last month"
          icon={Gift}
        />
        <StatsCard
          title="Client Rebates"
          value="0"
          change="+0% from last month"
          icon={Users}
        />
        <StatsCard
          title="Total Bonuses"
          value="$0.00"
          change="+0% from last month"
          icon={TrendingUp}
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 w-full">
            <div>
              <CardTitle>Rebate History</CardTitle>
              <CardDescription>
                View and manage all rebate and bonus configurations
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                {showFilters ? 'Hide Filter' : 'Show Filter'}
              </Button>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <InlineFilter
            showFilters={showFilters}
            onToggle={() => setShowFilters(!showFilters)}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFilterChange={setActiveFilters}
            onFiltersClear={() => setActiveFilters({})}
            onFiltersApply={() => {}}
            title="Filter Rebates"
          />
          <DataTable
            columns={columns}
            data={filteredRebates}
            searchValue={searchValue}
            searchPlaceholder="Search rebates..."
            onSearchChange={setSearchValue}
            onSearchReset={() => setSearchValue('')}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFiltersChange={() => {}}
            showColumnVisibility={true}
            showAddButton={false}
            showExportButton={false}
            initialPageSize={10}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default IBRebates;
